export interface DropZoneConfig {
  dropZoneId: string;
  fileInputId: string;
  stagedContainerId: string;
  fileListId: string;
  uploadBtnId: string;
  multiple?: boolean;
  theme?: 'blue' | 'emerald' | 'indigo'; // NEW: Color theme support
}

export class FileDropZone {
  private stagedFiles: File[] = [];
  private config: DropZoneConfig;
  private activeClasses: string[];

  constructor(config: DropZoneConfig) {
    this.config = config;
    
    // Define active classes based on theme (Darker background + Ring for visibility)
    const theme = config.theme || 'blue';
    const colorMap: Record<string, string[]> = {
      blue: ['border-blue-600', 'bg-blue-100', 'ring-4', 'ring-blue-200', 'scale-[1.01]'],
      emerald: ['border-emerald-600', 'bg-emerald-100', 'ring-4', 'ring-emerald-200', 'scale-[1.01]'],
      indigo: ['border-indigo-600', 'bg-indigo-100', 'ring-4', 'ring-indigo-200', 'scale-[1.01]']
    };
    
    this.activeClasses = colorMap[theme];
    this.init();
  }

  private init() {
    const dropZone = document.getElementById(this.config.dropZoneId);
    const fileInput = document.getElementById(this.config.fileInputId) as HTMLInputElement;

    if (!dropZone || !fileInput) return;

    // 1. Click to Open
    dropZone.addEventListener('click', () => fileInput.click());

    // 2. Handle Input Change
    fileInput.addEventListener('change', () => {
      if (fileInput.files) {
        this.addFiles(Array.from(fileInput.files));
        fileInput.value = '';
      }
    });

    // 3. Drag & Drop Visuals
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropZone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, false);
    });

    // Highlight on drag enter/over
    ['dragenter', 'dragover'].forEach(eventName => {
      dropZone.addEventListener(eventName, () => {
        dropZone.classList.add(...this.activeClasses);
        dropZone.classList.remove('border-gray-300'); // Optional: remove neutral border
      }, false);
    });

    // Remove highlight on leave/drop
    ['dragleave', 'drop'].forEach(eventName => {
      dropZone.addEventListener(eventName, () => {
        dropZone.classList.remove(...this.activeClasses);
        dropZone.classList.add('border-gray-300'); // Restore neutral border
      }, false);
    });

    // 4. Handle Drop
    dropZone.addEventListener('drop', (e: DragEvent) => {
      const dt = e.dataTransfer;
      if (dt && dt.files) {
        this.addFiles(Array.from(dt.files));
      }
    });
  }

  private addFiles(newFiles: File[]) {
    const unique = newFiles.filter(nf => 
      !this.stagedFiles.some(sf => sf.name === nf.name && sf.size === nf.size)
    );
    
    if (this.config.multiple === false) {
      this.stagedFiles = unique.slice(0, 1);
    } else {
      this.stagedFiles = [...this.stagedFiles, ...unique];
    }
    
    this.render();
  }

  private render() {
    const list = document.getElementById(this.config.fileListId);
    const container = document.getElementById(this.config.stagedContainerId);
    const btn = document.getElementById(this.config.uploadBtnId) as HTMLButtonElement;

    if (!list || !container) return;

    list.innerHTML = '';

    if (this.stagedFiles.length === 0) {
      container.classList.add('hidden');
      if (btn) {
        btn.textContent = 'Upload Files'; // Reset text
        btn.disabled = true;
      }
      return;
    }

    container.classList.remove('hidden');
    
    if (btn) {
      btn.textContent = `Upload ${this.stagedFiles.length} File${this.stagedFiles.length > 1 ? 's' : ''}`;
      btn.disabled = false;
    }

    // Determine icon color based on theme
    const theme = this.config.theme || 'blue';
    const textClass = `text-${theme}-600`;
    const bgClass = `bg-${theme}-100`; // Lighter background for icon container

    this.stagedFiles.forEach((file, index) => {
      const item = document.createElement('div');
      item.className = 'flex items-center justify-between bg-white p-2 border rounded-md shadow-sm mb-2 transition-all animate-in fade-in slide-in-from-top-1';
      item.innerHTML = `
        <div class="flex items-center gap-3 overflow-hidden">
          <div class="${bgClass} p-2 rounded-md">
            <svg class="w-5 h-5 ${textClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
          </div>
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-medium text-gray-700 truncate" title="${file.name}">${file.name}</span>
            <span class="text-xs text-gray-500">${(file.size / 1024).toFixed(1)} KB</span>
          </div>
        </div>
        <button class="remove-file-btn-${this.config.dropZoneId} text-gray-400 hover:text-red-500 p-1" data-index="${index}">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
      `;
      list.appendChild(item);
    });

    const removeBtns = document.querySelectorAll(`.remove-file-btn-${this.config.dropZoneId}`);
    removeBtns.forEach(b => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt((e.currentTarget as HTMLElement).dataset.index || '0');
        this.stagedFiles.splice(idx, 1);
        this.render();
      });
    });
  }

  public getFiles() {
    return this.stagedFiles;
  }

  public clear() {
    this.stagedFiles = [];
    this.render();
  }
}